/**
 * Form Validation and Cleaning Utilities
 *
 * This module contains utility functions for validating and cleaning email form data,
 * checking for unsaved changes, and ensuring data integrity.
 */

import type { MessageInfoAttachment } from '../../../api/cmail/cmail.dto';
import { isValidEmail } from './emailAddressUtils';

export interface FormState {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  attachments: MessageInfoAttachment[];
}

export interface CleanedFormData {
  to?: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
  attachments: MessageInfoAttachment[];
}

export interface FormValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Cleans form data by removing empty recipient fields and trimming whitespace
 * @param formData - Raw form data to clean
 * @returns Cleaned form data with optional recipient fields
 */
export const cleanFormData = (formData: FormState): CleanedFormData => {
  return {
    ...formData,
    to: formData.to.trim() || undefined,
    cc: formData.cc.trim() || undefined,
    bcc: formData.bcc.trim() || undefined,
    subject: formData.subject.trim(),
    body: formData.body.trim(),
  };
};

/**
 * Validates email form data for required fields and format
 * @param formData - Form data to validate
 * @param mode - Email mode (new, reply, forward)
 * @returns Validation result with errors and warnings
 */
export const validateEmailForm = (
  formData: FormState,
  mode: 'new' | 'reply' | 'reply-single' | 'forward' = 'new'
): FormValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check required fields
  if (!formData.to?.trim()) {
    errors.push('Recipient (To) is required');
  }

  if (!formData.subject?.trim()) {
    errors.push('Subject is required');
  }

  if (!formData.body?.trim()) {
    errors.push('Message body is required');
  }

  // Validate email addresses
  if (formData.to?.trim()) {
    const { invalid } = validateEmailAddresses(formData.to);
    if (invalid.length > 0) {
      errors.push(`Invalid email addresses in To field: ${invalid.join(', ')}`);
    }
  }

  if (formData.cc?.trim()) {
    const { invalid } = validateEmailAddresses(formData.cc);
    if (invalid.length > 0) {
      warnings.push(
        `Invalid email addresses in Cc field: ${invalid.join(', ')}`
      );
    }
  }

  if (formData.bcc?.trim()) {
    const { invalid } = validateEmailAddresses(formData.bcc);
    if (invalid.length > 0) {
      warnings.push(
        `Invalid email addresses in Bcc field: ${invalid.join(', ')}`
      );
    }
  }

  // Check subject length
  if (formData.subject?.trim() && formData.subject.trim().length > 255) {
    warnings.push('Subject is very long and may be truncated');
  }

  // Check body length
  if (formData.body?.trim() && formData.body.trim().length > 50000) {
    warnings.push('Message body is very long');
  }

  // Check attachment size limits
  const totalAttachmentSize = formData.attachments.reduce(
    (total, attachment) => total + (Number(attachment.docSize) || 0),
    0
  );
  const maxAttachmentSize = 25 * 1024 * 1024; // 25MB
  if (totalAttachmentSize > maxAttachmentSize) {
    errors.push('Total attachment size exceeds 25MB limit');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Validates a comma-separated list of email addresses
 * @param emailString - Comma-separated email addresses
 * @returns Object with valid and invalid email addresses
 */
export const validateEmailAddresses = (
  emailString: string
): {
  valid: string[];
  invalid: string[];
} => {
  const emails = emailString
    .split(',')
    .map((email) => email.trim())
    .filter((email) => email.length > 0);

  const valid: string[] = [];
  const invalid: string[] = [];

  emails.forEach((email) => {
    if (isValidEmail(email)) {
      valid.push(email.toLowerCase());
    } else {
      invalid.push(email);
    }
  });

  return { valid, invalid };
};

/**
 * Checks if there are unsaved changes in the form compared to the original
 * @param currentForm - Current form state
 * @param originalForm - Original form state
 * @param mode - Email mode
 * @param message - Original message (for draft comparison)
 * @returns True if there are unsaved changes, false otherwise
 */
export const hasUnsavedChanges = (
  currentForm: FormState,
  originalForm: FormState,
  mode: 'new' | 'reply' | 'reply-single' | 'forward' = 'new',
  message?: any
): boolean => {
  // For new emails, save draft if there's any content
  if (mode === 'new' && !message) {
    return (
      currentForm.subject.trim() !== '' ||
      currentForm.body.trim() !== '' ||
      currentForm.to.trim() !== '' ||
      currentForm.cc.trim() !== '' ||
      currentForm.bcc.trim() !== ''
    );
  }

  // For existing drafts or replies/forwards, compare against original form
  return (
    currentForm.to !== originalForm.to ||
    currentForm.cc !== originalForm.cc ||
    currentForm.bcc !== originalForm.bcc ||
    currentForm.subject !== originalForm.subject ||
    currentForm.body !== originalForm.body ||
    currentForm.attachments.length !== originalForm.attachments.length ||
    currentForm.attachments.some(
      (attachment, index) =>
        attachment.docId !== originalForm.attachments[index]?.docId
    )
  );
};

/**
 * Checks if the form has any content that would warrant saving as a draft
 * @param formData - Form data to check
 * @returns True if form has content worth saving, false otherwise
 */
export const hasDraftableContent = (formData: FormState): boolean => {
  return (
    formData.subject.trim() !== '' ||
    formData.body.trim() !== '' ||
    formData.to.trim() !== '' ||
    formData.cc.trim() !== '' ||
    formData.bcc.trim() !== '' ||
    formData.attachments.length > 0
  );
};

/**
 * Creates an empty form state
 * @returns Empty form state with default values
 */
export const createEmptyForm = (): FormState => {
  return {
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    attachments: [],
  };
};

/**
 * Merges form data, preferring non-empty values from the source
 * @param target - Target form to merge into
 * @param source - Source form to merge from
 * @returns Merged form data
 */
export const mergeFormData = (
  target: FormState,
  source: Partial<FormState>
): FormState => {
  return {
    to: source.to?.trim() || target.to,
    cc: source.cc?.trim() || target.cc,
    bcc: source.bcc?.trim() || target.bcc,
    subject: source.subject?.trim() || target.subject,
    body: source.body?.trim() || target.body,
    attachments: source.attachments || target.attachments,
  };
};

/**
 * Formats form data for display in a summary
 * @param formData - Form data to format
 * @returns Formatted summary string
 */
export const formatFormSummary = (formData: FormState): string => {
  const parts: string[] = [];

  if (formData.to) {
    parts.push(`To: ${formData.to}`);
  }
  if (formData.cc) {
    parts.push(`Cc: ${formData.cc}`);
  }
  if (formData.bcc) {
    parts.push(`Bcc: ${formData.bcc}`);
  }
  if (formData.subject) {
    parts.push(`Subject: ${formData.subject}`);
  }
  if (formData.attachments.length > 0) {
    parts.push(`Attachments: ${formData.attachments.length}`);
  }

  return parts.join(' | ');
};
